import { preferenceOptionToBaseTag } from '@hungryroot/preference-options';
import { type BaseTag, type CustomerTagList } from '@hungryroot/rest';
import {
  type GetPreferenceOptionsResponse,
  type PreferenceOptionKey,
} from '@hungryroot/rest/api/v2/preference_options/get/get_preference_options';

/** Must be a key on Quiz */
type PreferenceSectionKeys = 'foodQualities' | 'foodFormats';

/** Must be values that match `PreferenceOptionKey` */
const pageKeys = [
  'preference_options.food_quality',
  'preference_options.food_format',
] as const;

/** Map each preference option page to a section of the Quiz
 *
 * ```ts
 * 'my-cool-page': 'myCoolPage'
 * ```
 *
 * This means that the preference options under the page key `my-cool-page` will be stored in the `myCoolPage` section of the Quiz
 */
const pageToDataKeyMap: Record<
  (typeof pageKeys)[number],
  PreferenceSectionKeys
> = {
  'preference_options.food_quality': 'foodQualities',
  'preference_options.food_format': 'foodFormats',
};

/**
 * Create a map of tag ids to preference options for a given page
 *
 *
 * ```ts
 *
 * const preferenceOptions = {
 *  pages: [
 * {
 *   key: "my-cool-page",
 *   options: [{tag_id: "1", name: "Organic"
 * }...
 *
 * const pageKey = "my-cool-page";
 *
 * indexPreferenceOptionsPage(preferenceOptions)(pageKey) ===
 * {
 *  "1": {id: "1", name: "Organic"}
 * }
 * ```
 *
 * */
export const indexPreferenceOptionsPage =
  (preferenceOptions?: GetPreferenceOptionsResponse) =>
  (pageKey: PreferenceOptionKey) =>
    preferenceOptions?.pages
      .find((page) => page.key === pageKey)
      ?.options.map(preferenceOptionToBaseTag)
      .reduce(
        (seed, tag) => {
          seed[tag.id] = tag;
          return seed;
        },
        {} as Record<string, BaseTag>
      );

/** Process Quiz data for sections drive by the `preference-options` endpoint where the selected value is based on `preference` being `0/1`  */
export const processBinaryPreferenceOptions = (
  preferenceOptions: GetPreferenceOptionsResponse | undefined
) => {
  const preferenceOptionsIndexer =
    indexPreferenceOptionsPage(preferenceOptions);

  const data: Record<PreferenceSectionKeys, number[]> = {
    foodQualities: [],
    foodFormats: [],
  };

  const handleTag = (tag: CustomerTagList) => {
    pageKeys.forEach((pageKey) => {
      const indexedPreferenceOptions = preferenceOptionsIndexer(pageKey);
      if (indexedPreferenceOptions?.[tag.tag] !== undefined) {
        const dataKey = pageToDataKeyMap[pageKey];
        if (dataKey !== undefined) {
          if (tag.preference === 1) {
            data[dataKey].push(tag.tag);
          }
        }
      }
    });
  };

  return {
    data,
    handleTag,
  };
};
